import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup';
import {Form} from 'react-bootstrap';
import {toast} from 'react-toastify';
import { Button } from '@app/components';
import { deleteContractDocumentById, getContractDocumentById, getFMSContractByFranchiseId, updateFMSContract } from '@app/services/FMScontracts';
import moment from 'moment';
import PreviewModal from '@app/pages/ops-manual/PreviewModal';
import { confirmAlert } from 'react-confirm-alert';
import { getExtension } from '@app/services/opsManual';
import fileDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import { encodeId } from '@app/helpers/base64url';
import { useDecodedParam } from '@app/hooks/useDecodedParam';

const EditFMSContract = () => {

    let obj: any = {}
    const contractTerms = [{value:'2', label:'2 Years'}, {value:'5', label:'5 Years'}]
    const financingTypes = [{value: 'yes' , name:'Yes', isDisabled: false, isChecked: false}, {value: 'no', name:'No', isDisabled: false, isChecked: false}, {value: 'yes-but-failed', name:'Yes but failed', isDisabled: false, isChecked: false}]
    const subjectsTo = [{value:'0', label:'0'}, {value:'30', label:'30'}, {value:'50', label:'50'}, {value:'80', label:'80'}, {value:'100', label:'100'}]
    const [fileList, setFileList] = useState([]);
    const [fileError, setFileError] = useState('');
    const [contract, setContractData] = useState(obj);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [url, setUrl] = useState('');
    const [filename, setFileName] = useState('');
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const navigate = useNavigate();
    const canAccess = useSelector((state: any) => state.auth.permissions.franchise);
    const id = useDecodedParam('encodedId', '/franchise');

    useEffect(() => {
        if(!canAccess['edit-contract']){
            navigate('/franchise/view-contract/' + encodeId(id));
        }

        getContractByFranchiseId(id);

        return () => {
            setContractData(obj)
            setDocuments([])
        }
    }, []);

    const updateFMSContractAPI = async (payload) => {
        try {
            setLoading(true);
            const resp = await updateFMSContract(payload);
            if(resp.success === true){
                setLoading(false);
                toast.success(resp?.message);
                navigate('/franchise/view-contract/' + encodeId(id));
            }else{
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
          } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Something went wrong');
          }
    };

    const getContractByFranchiseId = async (id) => {
        try {
            setLoading(true);
            const resp = await getFMSContractByFranchiseId(id);
            if(resp.success === true){
                setLoading(false);
                if(Object.keys(resp?.collections?.contract).length > 0){
                    setContractData(resp?.collections?.contract);
                    setDocuments(resp?.collections?.contract?.documents.filter((doc) => doc.type == 'contract_documents'))
                }else{
                    toast.error(resp?.message || 'Something went wrong');
                    navigate('/franchise');
                }
            }else{
                setLoading(false);
                toast.error(resp?.message);
                navigate('/franchise');
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
        initialValues: {
            franchise_id: id,
            contract_term: contract?.contract_term,
            contract_initial_fee: contract?.contract_initial_fee,
            deposit_amount: contract?.deposit_amount,
            deposit_paid_date: contract?.deposit_paid_date ? moment(contract?.deposit_paid_date).format('YYYY-MM-DD') : '',
            plan_financed_amount: contract?.plan_financed_amount,
            financing: contract?.financing,
            special_payment_amount: contract?.special_payment_amount,
            monthly_franchise_fees: contract?.monthly_franchise_fees,
            monthly_student_fee_before: contract?.monthly_student_fee_before,
            monthly_student_fee_after: contract?.monthly_student_fee_after,
            subject_to: contract?.subject_to,
            monthly_student_fee_tier_two: contract?.monthly_student_fee_tier_two,
            renewal_fee: contract?.renewal_fee,
            renewal_date: contract?.renewal_date ? moment(contract?.renewal_date).format('YYYY-MM-DD') : '',
            contract_commencement_date: contract?.contract_commencement_date ? moment(contract?.contract_commencement_date).format('YYYY-MM-DD') : '',
            kashflow_billing_start_date: contract?.kashflow_billing_start_date ? moment(contract?.kashflow_billing_start_date).format('YYYY-MM-DD') : '',
            start_date: contract?.start_date ? moment(contract?.start_date).format('YYYY-MM-DD') : '',
            effective_start_date: contract?.effective_start_date ? moment(contract?.effective_start_date).format('YYYY-MM-DD') : '',
            end_date: contract?.end_date ? moment(contract?.end_date).format('YYYY-MM-DD') : '',
        },
        validationSchema: Yup.object({
            contract_term: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
            contract_initial_fee: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            deposit_amount: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Amount must be greater than or equal to 0").max(10000, 'Amount must be less than or equal to 10,000'),
            deposit_paid_date: Yup.string().nullable()
            .matches(/^(\d{4})(\/|-)(\d{1,2})(\/|-)(\d{1,2})$/, { message: 'Please enter a valid date', excludeEmptyString: true }),
            plan_financed_amount: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Amount must be greater than or equal to 0").max(10000, 'Amount must be less than or equal to 10,000'),
            financing: Yup.string().nullable(),
            special_payment_amount: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Amount must be greater than or equal to 0").max(10000, 'Amount must be less than or equal to 10,000'),
            monthly_franchise_fees: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            monthly_student_fee_before: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0,"Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            monthly_student_fee_after: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0,"Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            monthly_student_fee_tier_two: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0, "Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            subject_to: Yup.string().nullable(),
            renewal_fee: Yup.number().nullable().transform((v, o) => o === '' ? null : v).min(0,"Fee must be greater than or equal to 0").max(10000, 'Fee must be less than or equal to 10,000'),
            renewal_date: Yup.string().nullable(),
            contract_commencement_date: Yup.string().nullable()
            .matches(/^(\d{4})(\/|-)(\d{1,2})(\/|-)(\d{1,2})$/, { message: 'Please enter a valid date', excludeEmptyString: true }),
            kashflow_billing_start_date: Yup.string().nullable()
            .matches(/^(\d{4})(\/|-)(\d{1,2})(\/|-)(\d{1,2})$/, { message: 'Please enter a valid date', excludeEmptyString: true }),
        }),
        onSubmit: (values) => {
            delete values.start_date
            delete values.effective_start_date
            delete values.end_date

            const data = new FormData();
            if (fileList.length) {
                Array.from(fileList).forEach((file) => {
                    data.append("contract_documents", file);
                });
            }
            
            Object.keys(values).forEach(fieldName => {
                data.append(fieldName, values[fieldName] ?? '');
            })
            updateFMSContractAPI(data);
        },
        enableReinitialize: true
    });

    const handleContractDocumentChange = (e) => {
        if(e.target.files.length > 10){
            setFileError('You can only upload a maximum of 10 documents')
            setFileList([])
        }else{
            setFileError('')
            setFileList(e.target.files);
        }
        
        // const selectedFiles = Array.from(e.target.files);
        // let totalSize = selectedFiles.reduce((acc, file) => acc + file['size'], 0);
        // console.log(totalSize/1048576)
    };
    
    const previewFile = async (docId, name) => {
        try{
            const resp = await getContractDocumentById(docId);
            setUrl(URL.createObjectURL(resp))
            setFileName(name)
            handleShow();

        } catch (error) {
            setLoading(false);
            console.log(error)
        }
    }

    const downloadDocument = async (docId, name) => {
        try{
            setLoading(true);
            const resp = await getContractDocumentById(docId);
            fileDownload(resp, name);
            setLoading(false);        
        } catch (error) {
            setLoading(false);
            console.log(error)
        }
    }

    const deleteDocumentConfirm = (e, docId) => {
        confirmAlert({
            title: 'Confirm to submit',
            message: 'Are you sure you want to delete this document?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => deleteDocument(docId)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
        e.preventDefault();
    };

    const deleteDocument = async (docId) => {
        try {
            setLoading(true);
            const resp = await deleteContractDocumentById(docId);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                getContractByFranchiseId(id);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
          } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Something went wrong.');
          }
    };

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-primary">
                            <div className="card-header">
                                <h3 className="card-title">Update Franchise Agreement ({ contract?.name})</h3>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="card-body">
                                    <div className="row">
                                        <dt className="col-sm-3">Franchise Details</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='kashflow_billing_start_date'>Billing Start Date As Per Kashflow</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='kashflow_billing_start_date' 
                                                    name='kashflow_billing_start_date'
                                                    placeholder="Enter kashflow billing start date" 
                                                    value={moment(values.kashflow_billing_start_date).format('YYYY-MM-DD')}
                                                    // min={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.start_date).format('YYYY-MM-DD')}
                                                    // max={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.end_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                />
                                                {(touched.kashflow_billing_start_date && errors.kashflow_billing_start_date) && (
                                                    <div className="text-danger">
                                                        {errors.kashflow_billing_start_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Contract</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Contract Term 11.1 (Years)</label>
                                                <select
                                                    className="form-control"
                                                    name="contract_term"
                                                    onChange={handleChange} 
                                                    value={values.contract_term}
                                                >
                                                    <option value="">Please select</option>
                                                    {contractTerms.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {(touched.contract_term && errors.contract_term) && (
                                                    <div className="text-danger">
                                                        {errors.contract_term}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Contracted Initial Fee</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="exampleInputEmail1" 
                                                        placeholder="Enter initial fee"
                                                        name="contract_initial_fee" 
                                                        onChange={handleChange} 
                                                        value={values.contract_initial_fee}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                        />
                                                </div>
                                                {(touched.contract_initial_fee && errors.contract_initial_fee) && (
                                                    <div className="text-danger">
                                                        {errors.contract_initial_fee}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='contract_commencement_date'>Commencement Date As Per Contract</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='contract_commencement_date' 
                                                    name='contract_commencement_date'
                                                    placeholder="Enter contract commencement date" 
                                                    value={moment(values.contract_commencement_date).format('YYYY-MM-DD')}
                                                    // min={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.start_date).format('YYYY-MM-DD')}
                                                    // max={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.end_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                />
                                                {(touched.contract_commencement_date && errors.contract_commencement_date) && (
                                                    <div className="text-danger">
                                                        {errors.contract_commencement_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Renewal</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="renewal_fee">Renewal Fee (Under 'Term' in Contract)</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="renewal_fee" 
                                                        placeholder="Enter renewal fee"
                                                        name="renewal_fee" 
                                                        onChange={handleChange} 
                                                        value={values.renewal_fee}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.renewal_fee && errors.renewal_fee) && (
                                                    <div className="text-danger">
                                                        {errors.renewal_fee}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='renewal_date'>Renewal Date</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='renewal_date' 
                                                    name='renewal_date'
                                                    placeholder="Enter renewal date" 
                                                    value={moment(values.renewal_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                />
                                                {(touched.renewal_date && errors.renewal_date) && (
                                                    <div className="text-danger">
                                                        {errors.renewal_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Deposit</dt>
                                        
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Deposit As Per Proposed Letter</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="exampleInputEmail1" 
                                                        placeholder="Enter deposit amount"
                                                        name="deposit_amount" 
                                                        onChange={handleChange} 
                                                        value={values.deposit_amount}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.deposit_amount && errors.deposit_amount) && (
                                                    <div className="text-danger">
                                                        {errors.deposit_amount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='deposit_paid_date'>Deposit Paid Date</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='deposit_paid_date' 
                                                    name='deposit_paid_date'
                                                    placeholder="Enter deposit paid date" 
                                                    value={moment(values.deposit_paid_date).format('YYYY-MM-DD')}
                                                    // min={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.start_date).format('YYYY-MM-DD')}
                                                    // max={moment((batches.find(obj => { return obj.id == formik.values.batches[index].id }))?.end_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                />
                                                {(touched.deposit_paid_date && errors.deposit_paid_date) && (
                                                    <div className="text-danger">
                                                        {errors.deposit_paid_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Initial Fee Balance Due Arrangements</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="plan_financed_amount">Plan/Financed Amount <i className='fa fa-info-circle' title='By straight cash, as per proposed letter'/></label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="plan_financed_amount" 
                                                        placeholder="Enter plan financed amount"
                                                        name="plan_financed_amount" 
                                                        onChange={handleChange} 
                                                        value={values.plan_financed_amount}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.plan_financed_amount && errors.plan_financed_amount) && (
                                                    <div className="text-danger">
                                                        {errors.plan_financed_amount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="financing">Financing</label>
                                                {(financingTypes).map((obj) => (
                                                    <div className="form-check col-sm-12" key={obj?.value}>
                                                        <input 
                                                            className="form-check-input" 
                                                            id="financing"
                                                            type="radio" 
                                                            name="financing" 
                                                            onChange={ (e) => {
                                                                setFieldValue('financing', e.target.value)
                                                            }}
                                                            value={obj?.value}
                                                            checked={obj?.value == values.financing}
                                                            disabled={obj?.isDisabled}
                                                        />
                                                        <label className="form-check-label">{ obj?.name }</label><br/>
                                                    </div>
                                                )) }
                                                {(touched.financing && errors.financing) && (
                                                    <div className="text-danger">
                                                        {errors.financing}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="special_payment_amount">Special Payment Terms <i className='fa fa-info-circle' title='To claim initial fee portion via alt growth mechanism'/></label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="special_payment_amount" 
                                                        placeholder="Enter special payment amount"
                                                        name="special_payment_amount" 
                                                        onChange={handleChange} 
                                                        value={values.special_payment_amount}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.special_payment_amount && errors.special_payment_amount) && (
                                                    <div className="text-danger">
                                                        {errors.special_payment_amount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Standard Fees</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="monthly_franchise_fees">Monthly Franchise Fee</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="monthly_franchise_fees" 
                                                        placeholder="Enter monthly franchise fee"
                                                        name="monthly_franchise_fees" 
                                                        onChange={handleChange} 
                                                        value={values.monthly_franchise_fees}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.monthly_franchise_fees && errors.monthly_franchise_fees) && (
                                                    <div className="text-danger">
                                                        {errors.monthly_franchise_fees}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="monthly_student_fee_before">Monthly Student Fee Before</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="monthly_student_fee_before" 
                                                        placeholder="Enter monthly student before"
                                                        name="monthly_student_fee_before" 
                                                        onChange={handleChange} 
                                                        value={values.monthly_student_fee_before}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.monthly_student_fee_before && errors.monthly_student_fee_before) && (
                                                    <div className="text-danger">
                                                        {errors.monthly_student_fee_before}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="monthly_student_fee_after">Monthly Student Fee After</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="monthly_student_fee_after" 
                                                        placeholder="Enter monthly student fee after"
                                                        name="monthly_student_fee_after" 
                                                        onChange={handleChange} 
                                                        value={values.monthly_student_fee_after}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.monthly_student_fee_after && errors.monthly_student_fee_after) && (
                                                    <div className="text-danger">
                                                        {errors.monthly_student_fee_after}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Non-Standard Fees</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="monthly_student_fee_tier_two">Monthly Student Fee Tier 2</label>
                                                <div className='input-group'>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">{ contract?.currency }</span>
                                                    </div>
                                                    <Form.Control 
                                                        type="number" 
                                                        className="form-control" 
                                                        id="monthly_student_fee_tier_two" 
                                                        placeholder="Enter monthly student fee tier two"
                                                        name="monthly_student_fee_tier_two" 
                                                        onChange={handleChange} 
                                                        value={values.monthly_student_fee_tier_two}
                                                        onWheel={ (e) => e.currentTarget.blur() }
                                                    />
                                                </div>
                                                {(touched.monthly_student_fee_tier_two && errors.monthly_student_fee_tier_two) && (
                                                    <div className="text-danger">
                                                        {errors.monthly_student_fee_tier_two}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="subject_to">Subjects To</label>
                                                <select
                                                    className="form-control"
                                                    name="subject_to"
                                                    onChange={handleChange} 
                                                    value={values.subject_to}
                                                >
                                                    <option value="">Please select </option>
                                                    {subjectsTo.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {(touched.subject_to && errors.subject_to) && (
                                                    <div className="text-danger">
                                                        {errors.subject_to}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">FMS Dates</dt>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='start_date'> FMS Start Date (Management's reported date)</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='start_date' 
                                                    name='start_date'
                                                    placeholder="Enter date" 
                                                    value={moment(values.start_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                                {(touched.start_date && errors.start_date) && (
                                                    <div className="text-danger">
                                                        {errors.start_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='effective_start_date'> FMS Effective Start Date</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='effective_start_date' 
                                                    name='effective_start_date'
                                                    placeholder="Enter date" 
                                                    value={moment(values.effective_start_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                                {(touched.effective_start_date && errors.effective_start_date) && (
                                                    <div className="text-danger">
                                                        {errors.effective_start_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor='end_date'> FMS End Date</label>
                                                <Form.Control 
                                                    type="date" 
                                                    id='end_date' 
                                                    name='end_date'
                                                    placeholder="Enter date" 
                                                    value={moment(values.end_date).format('YYYY-MM-DD')}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                                {(touched.end_date && errors.end_date) && (
                                                    <div className="text-danger">
                                                        {errors.end_date}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="row">
                                        <dt className="col-sm-3">Contract Repository</dt>    
                                        <div className="col-sm-3">
                                            <div className="form-group">
                                                <label htmlFor="contract_documents">Upload Contract Document(s)</label>
                                                <div className="input-group">
                                                    <div className="custom-file">
                                                        <input type="file" className="custom-file-input" id="contract_documents" onChange={handleContractDocumentChange} multiple/>
                                                        <label className="custom-file-label" htmlFor="contract_documents">
                                                            {fileList.length > 0 ? `${fileList.length} file(s) selected` : 'Choose file'}
                                                        </label>
                                                    </div>
                                                    {/* <div className="input-group-append">
                                                        <span className="input-group-text">Upload</span>
                                                    </div> */}
                                                </div>
                                                <div className="text-danger">
                                                    {fileError}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2">Update </Button>
                                    <Link className="btn btn-default" to={'/franchise/view-contract/' + encodeId(id)}>Cancel</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card card-primary">
                            <div className="card-header">
                                <h3 className="card-title">Contract Documents</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {documents.length > 0 && (
                                        <table className="table table-bordered" id="client_id_table">
                                            <tbody>
                                                <tr className='text-center'>
                                                    <th>Document</th>
                                                    <th>Action</th>
                                                </tr>
                                                {documents.length > 0 && documents.map(file => (
                                                    <tr key={file?.id}>
                                                        <td>
                                                            <a href='javascript:void(0)' onClick={ (e) => { downloadDocument(file?.id, file?.document_name)} } data-toggle="tooltip" title="Preview" className='mx-3'> 
                                                                {file?.document_name}
                                                            </a>
                                                        </td>
                                                        <td className='pl-5'>
                                                            <a href='javascript:void(0)'
                                                                onClick={ (e) => { deleteDocumentConfirm(e, file?.id)} } 
                                                                data-toggle="tooltip" title="Delete Document">
                                                                <i className="fas fa-trash fa-sm mr-2"/>
                                                            </a>
                                                            <a href='javascript:void(0)' onClick={ (e) => { downloadDocument(file?.id, file?.document_name)} } data-toggle="tooltip" title="Download Document">
                                                                <i className="fas fa-download mr-2"></i>
                                                            </a> 
                                                            { (['docx', 'doc', 'xlsx', 'xls', 'wav'].findIndex((ext) => ext == getExtension(file?.document_name))) < 0 &&
                                                                <a href='javascript:void(0)'
                                                                onClick={ (e) => { previewFile(file?.id, file?.document_name)} }
                                                                    data-toggle="tooltip" title="Preview Document">
                                                                    <i className="fas fa-eye fa-sm mr-2"/>
                                                                </a>
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {documents.length == 0 && (
                                        <span className='text-danger'>No contract documents have been uploaded for this franchise.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <PreviewModal
            show={show}
            handleClose={handleClose}
            url={url}
            filename={filename}
        />
    </div>
  )
}

export default EditFMSContract

